import React, { useEffect, useMemo, useState } from 'react';
import { Grid, InputAdornment, MenuItem, Paper, TextField } from '@mui/material';
import { Box } from '@mui/system';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { toast } from 'react-toastify';
import { ConfirmationDialog, DataGrid, DatePicker, IconBtnBase } from 'Components';
import { CERTIFICATEVERIFICATIONLIST } from 'Constants/QueriesKeys';
import {
    CERTIFICATE_PER_PAGE,
    CERTIFICATE_PER_PAGE_OPTIONS,
    SEARCH_DEBOUNCE_MS,
    VERIFICATION_SORT_FIELDS,
    VERIFICATION_STATUSES,
} from 'Constants/CertificateNames';
import {
    deleteCertificateVerification,
    fetchCertificateVerifications,
} from 'Services/CertificateNamesSvc';
import VerificationStatusChip from './VerificationStatusChip';
import { formatDateTime, formatSimilarity, formatText } from './CertificateHelpers';

const styles = {
    tableSize: {
        height: 650,
        width: '100%',
    },
};

const ALL_STATUSES = '';

/**
 * Every name a visitor has put through the check, and how it came out.
 *
 * `registerRefetch` hands the page header a way to refresh whichever tab is
 * mounted - the two tabs own separate queries.
 */
const VerificationsTable = ({ registerRefetch }) => {
    const { t } = useTranslation();
    const spacing = 4;

    const [verificationList, setVerificationList] = useState([]);
    const [paginator1, setPaginator1] = useState(0);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(CERTIFICATE_PER_PAGE);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState(ALL_STATUSES);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [sortModel, setSortModel] = useState([]);
    const [recordToDelete, setRecordToDelete] = useState(null);

    const applySearch = useMemo(
        () =>
            debounce((value) => {
                setSearch(value);
                setPage(1);
            }, SEARCH_DEBOUNCE_MS),
        [],
    );

    useEffect(() => () => applySearch.cancel(), [applySearch]);

    // Anything outside the whitelist is a 422 from the API rather than an
    // ignored parameter, and a cleared sort (third header click) means no
    // sort_by at all.
    const activeSort = sortModel[0];
    const sortBy =
        activeSort?.sort && VERIFICATION_SORT_FIELDS.includes(activeSort.field)
            ? activeSort.field
            : undefined;
    const direction = sortBy ? activeSort.sort : undefined;

    const verificationListRender = useQuery(
        [
            CERTIFICATEVERIFICATIONLIST,
            { page, perPage, search, status, startDate, endDate, sortBy, direction },
        ],
        () =>
            fetchCertificateVerifications({
                page,
                per_page: perPage,
                search,
                status,
                // A half-filled range would silently drop every row, so it is
                // only sent once both ends are set.
                start_date: startDate && endDate ? startDate : undefined,
                end_date: startDate && endDate ? endDate : undefined,
                sort_by: sortBy,
                direction,
            }).then((list) => {
                setVerificationList(list.rows);
                setPaginator1(list.total);

                return list;
            }),
        { keepPreviousData: true },
    );

    const { refetch } = verificationListRender;

    useEffect(() => {
        registerRefetch(refetch);
    }, [registerRefetch, refetch]);

    const handleSearchChange = (event) => {
        setSearchInput(event.target.value);
        applySearch(event.target.value);
    };

    /**
     * Delete one attempt.
     *
     * The prints it produced go with it, so the toast names the count the server
     * reports rather than claiming only one row went. When the row was the last
     * one on its page, stepping back a page refetches through the query key —
     * refetching in place would leave an empty grid.
     */
    const handleDelete = async () => {
        const record = recordToDelete;
        setRecordToDelete(null);

        try {
            const { prints = 0 } = await deleteCertificateVerification(record.id);

            toast.success(
                prints
                    ? t('admin.certificateNames.history.deletedWithPrints', { prints })
                    : t('toast.success.deletedSuccessfully'),
            );

            if (verificationList.length === 1 && page > 1) {
                setPage(page - 1);
            } else {
                refetch();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message);
        }
    };

    const columns1 = [
        {
            field: 'action',
            headerName: t('common.actions'),
            width: 100,
            editable: false,
            sortable: false,
            renderCell: (row) => (
                <div className="flex items-center">
                    <IconBtnBase
                        title={t('common.delete')}
                        onClick={() => setRecordToDelete(row?.row)}
                        size="large"
                    >
                        <DeleteOutlineOutlinedIcon />
                    </IconBtnBase>
                </div>
            ),
        },
        {
            field: 'entered_name',
            headerName: t('admin.certificateNames.history.enteredName'),
            minWidth: 250,
            flex: 1,
            editable: false,
        },
        {
            field: 'matched_name',
            headerName: t('admin.certificateNames.history.matchedName'),
            minWidth: 250,
            flex: 1,
            editable: false,
            renderCell: (row) => formatText(row.row.matched_name),
        },
        {
            field: 'similarity',
            headerName: t('admin.certificateNames.history.similarity'),
            width: 120,
            editable: false,
            renderCell: (row) => formatSimilarity(row.row.similarity),
        },
        {
            field: 'status',
            headerName: t('admin.certificateNames.history.status'),
            width: 170,
            editable: false,
            renderCell: (row) => <VerificationStatusChip status={row.row.status} />,
        },
        {
            field: 'print_count',
            headerName: t('admin.certificateNames.history.printCount'),
            width: 110,
            editable: false,
            // Derived on the backend, not a sortable column on this endpoint.
            sortable: false,
        },
        {
            field: 'created_at',
            headerName: t('admin.certificateNames.history.dateTime'),
            width: 180,
            editable: false,
            renderCell: (row) => formatDateTime(row.row.created_at),
        },];

    return (
        <>
            <div className="mt-8">
                <Grid container spacing={spacing} alignItems="flex-end">
                    <Grid item xs={12} md={5}>
                        <h6 className="labelForm pb-2">{t('admin.certificateNames.history.search')}</h6>
                        <TextField
                            fullWidth
                            id="certificate-verifications-search"
                            placeholder={t('admin.certificateNames.history.searchPlaceholder')}
                            onChange={handleSearchChange}
                            value={searchInput}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <h6 className="labelForm pb-2">{t('admin.certificateNames.history.status')}</h6>
                        <TextField
                            select
                            fullWidth
                            id="certificate-verifications-status"
                            value={status}
                            onChange={(event) => {
                                setStatus(event.target.value);
                                setPage(1);
                            }}
                            // MUI renders a blank box for an empty value unless told
                            // to show the option, which would hide "All statuses".
                            SelectProps={{ displayEmpty: true }}
                        >
                            <MenuItem value={ALL_STATUSES}>
                                {t('admin.certificateNames.history.allStatuses')}
                            </MenuItem>
                            {VERIFICATION_STATUSES.map((value) => (
                                <MenuItem key={value} value={value}>
                                    {t(`admin.certificateNames.statuses.${value}`)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <h6 className="labelForm pb-2">
                            {t('admin.certificateNames.history.startDate')}
                        </h6>
                        <DatePicker
                            value={startDate}
                            onChange={(newValue) => {
                                if (dayjs(newValue).isAfter(endDate) || dayjs(newValue).isSame(endDate)) {
                                    setEndDate(null);
                                }
                                setStartDate(newValue ? dayjs(newValue).format('YYYY-MM-DD') : null);
                                setPage(1);
                            }}
                        />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <h6 className="labelForm pb-2">{t('admin.certificateNames.history.endDate')}</h6>
                        <DatePicker
                            value={endDate}
                            onChange={(newValue) => {
                                setEndDate(newValue ? dayjs(newValue).format('YYYY-MM-DD') : null);
                                setPage(1);
                            }}
                            disabled={!startDate}
                            minDate={dayjs(startDate)}
                        />
                    </Grid>
                </Grid>

                <Box className="mt-8" sx={styles.tableSize}>
                    <Grid container spacing={spacing}>
                        <Grid item xs={12}>
                            <Paper>
                                <DataGrid
                                    rowCount={paginator1}
                                    pagination
                                    paginationMode="server"
                                    sortingMode="server"
                                    sortModel={sortModel}
                                    onSortModelChange={(newSortModel) => {
                                        setSortModel(newSortModel);
                                        setPage(1);
                                    }}
                                    page={page - 1}
                                    onPageChange={(newPage) => setPage(newPage + 1)}
                                    pageSize={perPage}
                                    rowsPerPageOptions={CERTIFICATE_PER_PAGE_OPTIONS}
                                    onPageSizeChange={(newPageSize) => {
                                        setPerPage(newPageSize);
                                        setPage(1);
                                    }}
                                    loading={verificationListRender?.isLoading}
                                    rows={verificationList}
                                    columns={columns1}
                                />
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>
            </div>

            <ConfirmationDialog
                isOpen={Boolean(recordToDelete)}
                title={t('admin.certificateNames.history.deleteVerificationTitle')}
                text={t('admin.certificateNames.history.deleteVerificationText')}
                onReject={() => setRecordToDelete(null)}
                onResolve={handleDelete}
            />
        </>
    );
};

export default VerificationsTable;
